/* YoloLife — find products by the end or middle of a SKU.
 * Shopify search matches whole SKUs and prefixes only: "A007591" and "A00759" find the product,
 * "7591", "007591" and "591" return 0 results (tested on yololife.ae 2026-11-02). This file matches
 * fragments in the browser against the theme's SKU index (templates/collection.sku-index.liquid).
 *
 * - Pure helpers (normalize / isSkuQuery / matchSkus / skuParts / highlightSku) are exported for
 *   node:test: scripts/theme/__tests__/sku-search.test.cjs requires this file.
 * - load(): fetches the index the first time a query needs it and keeps it in sessionStorage for
 *   about an hour. Any storage or network failure leaves the SKU rows out; Shopify search is untouched.
 * - Search results page: fills [data-yl-sku-results] (sections/search.liquid).
 * - Header dropdown: assets/yl-predictive-search.js calls window.YoloLifeSkuSearch.
 */
(function (global) {
  'use strict';

  var MIN_LENGTH = 3;
  var LIMIT = 8;
  var RANK_SUFFIX = 0;
  var RANK_PREFIX = 1;
  var RANK_CONTAINS = 2;
  var IGNORED = /[\s-]/;

  // Shoppers type "a00 7591" or "A-007591"; SKUs are compared without spaces / hyphens, upper case.
  function normalize(value) {
    return String(value == null ? '' : value).replace(/[\s-]+/g, '').toUpperCase();
  }

  // At least 3 characters and one digit: "ab" or "tank" stay with Shopify search only.
  function isSkuQuery(term) {
    var query = normalize(term);
    return query.length >= MIN_LENGTH && /\d/.test(query);
  }

  // 0 = SKU ends with the query, 1 = starts with it, 2 = contains it, -1 = no match.
  // The end check comes first, so a whole-SKU match ranks as an end match.
  function rankOf(sku, query) {
    if (!query || sku.length < query.length) return -1;
    if (sku.slice(sku.length - query.length) === query) return RANK_SUFFIX;
    if (sku.slice(0, query.length) === query) return RANK_PREFIX;
    return sku.indexOf(query) !== -1 ? RANK_CONTAINS : -1;
  }

  function productKey(item) {
    if (item.product_id != null) return 'id:' + item.product_id;
    return 'url:' + String(item.url || '').split('?')[0];
  }

  /**
   * Variants whose SKU contains the query, best first: end match > start match > middle match;
   * in stock before sold out within a rank; then index order. One row per product, at most 8.
   * @param {Array<{sku:string, product_id:number, available:boolean}>} items SKU index entries.
   * @param {string} term What the shopper typed.
   * @param {{limit?:number, excludeProductIds?:Array<string|number>}} [options]
   *   excludeProductIds: products already shown by Shopify search (not repeated).
   */
  function matchSkus(items, term, options) {
    var query = normalize(term);
    if (!isSkuQuery(query) || !Array.isArray(items)) return [];
    var opts = options || {};
    var limit = opts.limit > 0 ? opts.limit : LIMIT;
    var excluded = {};
    (opts.excludeProductIds || []).forEach(function (id) { excluded['id:' + id] = true; });

    var hits = [];
    items.forEach(function (item, order) {
      if (!item || item.sku == null) return;
      var rank = rankOf(normalize(item.sku), query);
      if (rank !== -1) hits.push({ item: item, rank: rank, order: order });
    });
    hits.sort(function (a, b) {
      return a.rank - b.rank ||
        (b.item.available ? 1 : 0) - (a.item.available ? 1 : 0) ||
        a.order - b.order;
    });

    var seen = {};
    var matches = [];
    for (var i = 0; i < hits.length && matches.length < limit; i++) {
      var key = productKey(hits[i].item);
      if (seen[key] || excluded[key]) continue;
      seen[key] = true;
      matches.push(hits[i].item);
    }
    return matches;
  }

  // Splits the SKU as printed around the matched characters, so "SET-NW-A007591-01" + "7591"
  // highlights "7591" even though matching ignored the hyphens.
  function skuParts(sku, term) {
    var raw = String(sku == null ? '' : sku);
    var query = normalize(term);
    var map = [];
    var flat = '';
    for (var i = 0; i < raw.length; i++) {
      if (IGNORED.test(raw.charAt(i))) continue;
      map.push(i);
      flat += raw.charAt(i).toUpperCase();
    }
    var rank = rankOf(flat, query);
    if (rank === -1) return { before: raw, match: '', after: '' };
    var start = rank === RANK_SUFFIX ? flat.length - query.length : flat.indexOf(query);
    var from = map[start];
    var to = map[start + query.length - 1] + 1;
    return { before: raw.slice(0, from), match: raw.slice(from, to), after: raw.slice(to) };
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function highlightSku(sku, term) {
    var parts = skuParts(sku, term);
    return escapeHtml(parts.before) +
      (parts.match ? '<mark>' + escapeHtml(parts.match) + '</mark>' : '') +
      escapeHtml(parts.after);
  }

  // Links come from our own template; still only accept site-relative or http(s) URLs in href/src.
  function safeUrl(value) {
    var url = String(value == null ? '' : value);
    return /^(https?:)?\/\//i.test(url) || /^\/(?!\/)/.test(url) ? url : '';
  }

  var api = {
    MIN_LENGTH: MIN_LENGTH,
    LIMIT: LIMIT,
    normalize: normalize,
    isSkuQuery: isSkuQuery,
    matchSkus: matchSkus,
    skuParts: skuParts,
    highlightSku: highlightSku,
    escapeHtml: escapeHtml,
    safeUrl: safeUrl
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (!global || !global.document) return;

  // ---------- Browser: index loading + cache ----------
  var INDEX_VERSION = 1;              // bump when the index JSON changes shape → old caches are ignored
  var CACHE_TTL_MS = 60 * 60 * 1000;  // about an hour; new products / price changes show up after that
  var MAX_PAGES = 20;                 // 20 × 250 = 5,000 products; the catalog was 425 on 2026-11-02
  var memo = null;                    // { key, items, t }
  var pending = null;                 // { key, promise }

  function shopify() { return global.Shopify || {}; }
  function root() {
    var routes = shopify().routes;
    return (routes && routes.root) || '/';
  }
  // Titles, links (/zh-cn/…) and price text differ per language / country / currency.
  function cacheKey() {
    var s = shopify();
    var currency = (s.currency && s.currency.active) || '';
    return 'yl-sku-index:v' + INDEX_VERSION + ':' + root() + ':' + (s.country || '') + ':' + currency;
  }
  function indexUrl(page) {
    return root() + 'collections/all?view=sku-index&page=' + page;
  }
  function fresh(t) {
    var age = Date.now() - t;
    return age >= 0 && age < CACHE_TTL_MS;
  }

  function readCache(key) {
    var raw = null;
    try { raw = global.sessionStorage.getItem(key); } catch (e) { return null; }
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      if (data && data.v === INDEX_VERSION && Array.isArray(data.items) && fresh(data.t)) {
        return { items: data.items, t: data.t };
      }
    } catch (e) { /* unreadable entry: fall through and drop it */ }
    try { global.sessionStorage.removeItem(key); } catch (e) { /* storage blocked */ }
    return null;
  }
  function writeCache(key, items, t) {
    try {
      global.sessionStorage.setItem(key, JSON.stringify({ v: INDEX_VERSION, t: t, items: items }));
    } catch (e) { /* quota / private mode: keep the in-memory copy only */ }
  }

  // No "Accept: application/json" header: with it the storefront answers with the collection's own
  // JSON ({"collection":{…}}) instead of rendering the view template (实测 2026-11-02, theme dev).
  function fetchPage(page) {
    return fetch(indexUrl(page), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('sku-index page ' + page + ' HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var data = JSON.parse(text);
        if (!data || !Array.isArray(data.items)) throw new Error('sku-index page ' + page + ' has no items');
        return data;
      });
  }

  // The index if it is already in memory or sessionStorage, else null (no network).
  function peek() {
    var key = cacheKey();
    if (memo && memo.key === key && fresh(memo.t)) return memo.items;
    var cached = readCache(key);
    if (!cached) return null;
    memo = { key: key, items: cached.items, t: cached.t };
    return memo.items;
  }

  // Resolves with every index entry; one request chain at a time, shared by all callers.
  function load() {
    var cached = peek();
    if (cached) return Promise.resolve(cached);
    var key = cacheKey();
    if (pending && pending.key === key) return pending.promise;
    var promise = fetchPage(1)
      .then(function (first) {
        var pages = Math.min(Math.max(parseInt(first.pages, 10) || 1, 1), MAX_PAGES);
        var rest = [];
        // Read-only storefront GETs, so the remaining pages load in parallel (the serial-only rule
        // in CLAUDE.md is for writes to Shopify).
        for (var page = 2; page <= pages; page++) rest.push(fetchPage(page));
        return Promise.all(rest).then(function (others) {
          return others.reduce(function (all, data) { return all.concat(data.items); }, first.items);
        });
      })
      .then(function (items) {
        var t = Date.now();
        memo = { key: key, items: items, t: t };
        writeCache(key, items, t);
        pending = null;
        return items;
      }, function (error) {
        pending = null;
        if (global.console && global.console.debug) global.console.debug('[yl-sku-search] index unavailable', error);
        throw error;
      });
    pending = { key: key, promise: promise };
    return promise;
  }

  api.peek = peek;
  api.load = load;

  // ---------- Browser: search results page ----------
  function handleOf(url) {
    var match = /\/products\/([^/?#]+)/.exec(String(url || ''));
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch (e) { return match[1]; }
  }

  // Products already in Shopify's result grid, as index product ids (cards carry links, not ids).
  function productIdsOnPage(items) {
    var handles = {};
    document.querySelectorAll('[data-yl-collection-results] a[href*="/products/"]').forEach(function (link) {
      var handle = handleOf(link.getAttribute('href'));
      if (handle) handles[handle] = true;
    });
    var ids = [];
    items.forEach(function (item) {
      if (item && handles[handleOf(item.url)]) ids.push(item.product_id);
    });
    return ids;
  }

  function resultRow(item, term, text) {
    var href = safeUrl(item.url);
    if (!href) return '';
    var image = safeUrl(item.image);
    var thumb = image
      ? '<img src="' + escapeHtml(image) + '" alt="" width="56" height="56" loading="lazy" decoding="async">'
      : '';
    var soldOut = item.available === false && text.soldOut
      ? '<span class="yl-sku-results__soldout">' + escapeHtml(text.soldOut) + '</span>'
      : '';
    return '<li class="yl-sku-results__item">' +
      '<a class="yl-sku-results__link" href="' + escapeHtml(href) + '">' +
      '<span class="yl-sku-results__thumb" aria-hidden="true">' + thumb + '</span>' +
      '<span class="yl-sku-results__info">' +
      '<span class="yl-sku-results__name">' + escapeHtml(item.title) + '</span>' +
      '<span class="yl-sku-results__sku">' + escapeHtml(text.label) + ' ' + highlightSku(item.sku, term) + '</span>' +
      '</span>' +
      '<span class="yl-sku-results__meta">' +
      '<span class="yl-sku-results__price">' + escapeHtml(item.price) + '</span>' + soldOut +
      '</span>' +
      '</a></li>';
  }

  // Tells the page what the SKU list is doing, so the "nothing found" card never shows next to SKU
  // matches (user 2026-11-02: "上面有结果，下面说没有找到"). sections/search.liquid hides the empty card
  // while loading and when found, and the best sellers when found; on no match or failure it shows again.
  function markPage(host, state) {
    var page = host.closest('.yl-search');
    if (!page) return;
    if (state) page.setAttribute('data-yl-sku', state);
    else page.removeAttribute('data-yl-sku');
  }

  // The same product card as the result grid, rendered by templates/product.card.liquid
  // (/products/<handle>?view=card). Resolves to the card element, or null if it cannot be used.
  function fetchCard(item) {
    var href = safeUrl(item.url);
    if (!href) return Promise.resolve(null);
    var url = new URL(href, global.location.origin);
    url.searchParams.set('view', 'card');
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.text() : ''; })
      .then(function (html) {
        // Without the card template Shopify answers with the full product page, whose related-product
        // cards are other products: only accept a card that links to this product.
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var card = doc.querySelector('.yl-species-card');
        var link = card && card.querySelector('a[href*="/products/"]');
        return link && handleOf(link.getAttribute('href')) === handleOf(href) ? card : null;
      }, function () { return null; });
  }

  function showHeading(host, term, text, body) {
    // Function replacement: a typed "$&" must not be read as a replacement pattern.
    var heading = String(text.heading || '').replace('%terms%', function () { return term.trim(); });
    host.innerHTML = '<h2 class="yl-sku-results__title" id="yl-sku-results-title">' + escapeHtml(heading) + '</h2>';
    host.appendChild(body);
    host.setAttribute('aria-labelledby', 'yl-sku-results-title');
    host.hidden = false;
    markPage(host, 'found');
  }

  function renderResults(host, term, text, items) {
    var matches = matchSkus(items, term, { excludeProductIds: productIdsOnPage(items) });
    if (!matches.length) {
      host.innerHTML = '';
      host.hidden = true;
      markPage(host, '');
      return;
    }
    // Product cards when every card loads (few requests: at most LIMIT); otherwise the compact rows,
    // so a failed request never hides a match.
    Promise.all(matches.map(fetchCard)).then(function (cards) {
      var complete = cards.every(Boolean);
      var body;
      if (complete) {
        body = document.createElement('div');
        body.className = 'yl-species__grid yl-species__grid--grid-4';
        cards.forEach(function (card, i) {
          var node = document.importNode(card, true);
          // Show which SKU matched under the product name (the card itself has no SKU line).
          var title = node.querySelector('.yl-species-card__title');
          if (title && matches[i].sku) {
            var line = document.createElement('span');
            line.className = 'yl-sku-results__sku';
            line.innerHTML = escapeHtml(text.label) + ' ' + highlightSku(matches[i].sku, term);
            title.insertAdjacentElement('afterend', line);
          }
          body.appendChild(node);
        });
      } else {
        body = document.createElement('ul');
        body.className = 'yl-sku-results__list';
        body.setAttribute('role', 'list');
        body.innerHTML = matches.map(function (item) { return resultRow(item, term, text); }).join('');
      }
      showHeading(host, term, text, body);
    });
  }

  function initResultsPage() {
    var host = document.querySelector('[data-yl-sku-results]');
    if (!host) return;
    var term = host.getAttribute('data-terms') || '';
    if (!isSkuQuery(term)) return;
    var text = {
      heading: host.getAttribute('data-heading') || '',
      label: host.getAttribute('data-label') || 'SKU',
      loading: host.getAttribute('data-loading') || '',
      soldOut: host.getAttribute('data-sold-out') || ''
    };
    var cached = peek();
    if (cached) {
      renderResults(host, term, text, cached);
      return;
    }
    host.innerHTML = '<p class="yl-sku-results__status" role="status">' + escapeHtml(text.loading) + '</p>';
    host.hidden = false;
    markPage(host, 'loading');
    load().then(function (items) {
      renderResults(host, term, text, items);
    }, function () {
      host.innerHTML = '';
      host.hidden = true;
      markPage(host, '');
    });
  }

  global.YoloLifeSkuSearch = api;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initResultsPage, { once: true });
  } else {
    initResultsPage();
  }
})(typeof window !== 'undefined' ? window : this);
